import { checkContactExistsInInboxData } from "../redux/selectors";
import { convertMessageTimestamps } from "./timestampUtils";

// Read the store off window (see redux/store.js) instead of importing it directly.
// store.js -> authSlice.js -> socketSharedState.js already form an import cycle
// (authSlice imports clearCachedUserData from here), so a static import here caused
// an intermittent "Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization"
// crash depending on which module the bundler happened to touch first.
const getStore = () => (typeof window !== 'undefined' ? window.store : undefined);
export { getStore };

// ─── mutable shared state (object so cross-module mutations are visible) ──────
export const socketState = {
  socket: null,
  activeChat: null,
  dispatch: null,
  isDispatchReady: false,
  eventQueue: [],
  isProcessingQueue: false,
  lastMessageCardUpdate: new Map(),
  cachedUserData: null,
  userDataCacheTime: 0,
};

const USER_DATA_CACHE_TIME = 30000;
export const DEBOUNCE_TIME = 200;

// ─── user data cache ──────────────────────────────────────────────────────────
export const getCachedUserData = () => {
  const now = Date.now();
  if (socketState.cachedUserData && (now - socketState.userDataCacheTime) < USER_DATA_CACHE_TIME) {
    return socketState.cachedUserData;
  }
  try {
    const state = getStore().getState();
    const user = state?.auth?.user || null;
    socketState.cachedUserData = user;
    socketState.userDataCacheTime = now;
    return user;
  } catch (error) {
    console.warn('Failed to get user from Redux:', error);
    return null;
  }
};

// Clears the cached user snapshot — call on logout so a fast logout/login
// cycle can't briefly reuse the previous account's cached user data.
export const clearCachedUserData = () => {
  socketState.cachedUserData = null;
  socketState.userDataCacheTime = 0;
};

// ─── account id helpers ───────────────────────────────────────────────────────
export function getSocketEventAccountId(raw) {
  if (!raw || typeof raw !== "object") return null;
  const candidates = [
    raw.AccountID, raw.accountId,
    raw.fullMessage?.AccountID, raw.fullMessage?.accountId,
    raw.chat?.AccountID, raw.message?.AccountID,
  ];
  for (const v of candidates) {
    if (v != null && v !== "") return String(v);
  }
  return null;
}

export function shouldIgnoreSocketPayloadWrongAccount(raw) {
  const ev = getSocketEventAccountId(raw);
  const u = getCachedUserData();
  const cur = u?.accountId ?? u?.AccountID;
  if (!ev || cur == null || cur === "") return false;
  return ev !== String(cur);
}

export function shouldPlayInboxAlertForCurrentAccount(raw) {
  const ev = getSocketEventAccountId(raw);
  const u = getCachedUserData();
  const cur = u?.accountId ?? u?.AccountID;
  if (!ev || cur == null || cur === "") return false;
  return ev === String(cur);
}

// ─── sender field helpers ─────────────────────────────────────────────────────
/** Outbound team rows must not carry contact clientname or they render on the left in ChatCard. */
export function sanitizeOutboundChatDirection(chat) {
  if (!chat) return chat;
  const sid = chat.senderid ?? chat.senderId;
  const cid = chat.clientid ?? chat.clientId;
  if (sid != null && sid !== "" && (cid == null || cid === "")) {
    delete chat.clientname;
    delete chat.clientName;
  }
  return chat;
}

export function mergeResolvedSenderFieldsOntoChat(chat, normalizedMessage, rawMessage) {
  if (!chat) return;
  const senderIds = [
    chat.senderid, chat.senderId,
    normalizedMessage?.chat?.senderid, normalizedMessage?.chat?.senderId,
    normalizedMessage?.message?.senderid, normalizedMessage?.message?.senderId,
    normalizedMessage?.senderid, normalizedMessage?.senderId,
    rawMessage?.chat?.senderid, rawMessage?.chat?.senderId,
    rawMessage?.message?.senderid, rawMessage?.message?.senderId,
    rawMessage?.fullMessage?.chat?.senderid, rawMessage?.fullMessage?.chat?.senderId,
    rawMessage?.fullMessage?.message?.senderid, rawMessage?.fullMessage?.message?.senderId,
    rawMessage?.senderid, rawMessage?.senderId,
  ];
  const resolvedId = senderIds.find((v) => v != null && v !== "");
  if (resolvedId != null && !chat.senderid) chat.senderid = resolvedId;

  const senderNames = [
    chat.sendername, normalizedMessage?.chat?.sendername,
    normalizedMessage?.message?.sendername, normalizedMessage?.sendername,
    rawMessage?.chat?.sendername, rawMessage?.message?.sendername,
    rawMessage?.fullMessage?.chat?.sendername, rawMessage?.fullMessage?.message?.sendername,
    rawMessage?.sendername,
  ].filter((n) => n != null && n !== "" && n !== "Automated");
  const resolvedName = senderNames.find((n) => n != null && n !== "");
  if (resolvedName && !chat.sendername) chat.sendername = resolvedName;
  sanitizeOutboundChatDirection(chat);
}

export function isWhatsAppInboxPlatform(platform) {
  return platform === undefined || platform === null || platform === "whatsapp" || platform === "qr";
}

export function applyWorkflowAutomatedMetadata(chat, platform, isWorkflowMessage, isInternalMention) {
  if (!chat || !isWorkflowMessage || chat.clientid || chat.clientname || isInternalMention) return;
  // If performedBy is set, the frontend uses it directly — don't override with legacy heuristics
  if (chat.performedBy) return;
  if (isWhatsAppInboxPlatform(platform)) {
    chat.isWorkflowAutomated = true;
    if (chat.sendername === "Automated") delete chat.sendername;
  } else {
    chat.sendername = "Automated";
  }
}

// ─── dispatch helpers ─────────────────────────────────────────────────────────
export const getDispatch = () => {
  if (typeof socketState.dispatch === 'function') return socketState.dispatch;
  const store = getStore();
  if (store && typeof store.dispatch === 'function') return store.dispatch;
  return null;
};

export const ensureDispatchReady = () => {
  const dispatchFn = getDispatch();
  if (dispatchFn && typeof dispatchFn === 'function') {
    if (!socketState.isDispatchReady) {
      socketState.isDispatchReady = true;
      processEventQueue();
    }
    return true;
  }
  return false;
};

export const queueEvent = (eventType, eventData, handler) => {
  socketState.eventQueue.push({ type: eventType, data: eventData, handler, timestamp: Date.now() });
  if (ensureDispatchReady()) processEventQueue();
};

export const processEventQueue = async () => {
  if (socketState.isProcessingQueue || !socketState.isDispatchReady) return;
  socketState.isProcessingQueue = true;
  try {
    while (socketState.eventQueue.length > 0) {
      const event = socketState.eventQueue.shift();
      if (event && event.handler && typeof event.handler === 'function') {
        try { event.handler(event.data); } catch (_) {}
      }
    }
  } finally {
    socketState.isProcessingQueue = false;
  }
};

// ─── workspace / inbox helpers ────────────────────────────────────────────────
export const inboxCardWorkspaceIdFrom = (...sources) => {
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i];
    if (s == null) continue;
    const w = s.workspaceId ?? s.workspaceID;
    if (w != null && w !== "") return w;
  }
  return undefined;
};

// The QR number (connection) a live event belongs to, as { connectionId } -- or {} when none of the sources carries one (Meta,
// Instagram, Facebook, website, Shopify chats have no number). Spread into a hand-built inbox card so the number filter can tell
// which number the card is for (see addMessageCard in inboxRecentSlice.js).
export const inboxCardConnectionFields = (...sources) => {
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i];
    if (s == null) continue;
    const c = s.connectionId ?? s.chat?.connectionId ?? s.chat?.waConnectionId ?? s.fullMessage?.connectionId ?? s.message?.connectionId;
    if (c != null && c !== "") return { connectionId: String(c) };
  }
  return {};
};

export const messageWorkspaceMatchesCurrent = (wsFromMessage) => {
  const store = getStore();
  if (!store || typeof store.getState !== "function") return true;
  const currentWs = store.getState().workspace?.currentWorkspace?._id;
  if (currentWs == null || currentWs === "") return true;
  if (wsFromMessage == null || wsFromMessage === "") return true;
  const a = String(currentWs);
  const b = typeof wsFromMessage === "object" && wsFromMessage != null && typeof wsFromMessage.toString === "function"
    ? wsFromMessage.toString()
    : String(wsFromMessage);
  return a === b;
};

export const resolveIsForActiveChat = (contactIdFromMsg, ...workspaceSources) => {
  if (!socketState.activeChat || !contactIdFromMsg) return false;
  if (String(socketState.activeChat) !== String(contactIdFromMsg)) return false;
  const msgWs = inboxCardWorkspaceIdFrom(...workspaceSources);
  return messageWorkspaceMatchesCurrent(msgWs);
};

export const shouldSocketApplySelectedContactId = (contactId) => {
  const store = getStore();
  if (!contactId || !store || typeof store.getState !== "function") return false;
  const sel = store.getState().inboxAll?.selectedContactID;
  if (sel == null) return false;
  const inboxData = store.getState().inboxRecent?.data;
  if (inboxData && !checkContactExistsInInboxData(inboxData, contactId)) return false;
  return true;
};

export const normalizeId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object" && typeof value.toString === "function") return value.toString();
  return String(value);
};

export const getAssignedTargetFromPayload = (payload = {}) => {
  const assignedToId = normalizeId(
    payload.assignedToId ?? payload.assignedTo ?? payload.chat?.assignedToId ?? payload.chat?.assignedTo
  );
  const assignedToTypeRaw = payload.assignedToType ?? payload.chat?.assignedToType ?? "";
  const assignedToType = String(assignedToTypeRaw).toLowerCase();
  const isUnassigned = !assignedToId || assignedToType === "unassigned" || assignedToId.toLowerCase() === "unassigned";
  return { assignedToId, assignedToType, isUnassigned };
};

export const shouldAllowRealtimeInboxCardUpdate = (messageCardData) => {
  const store = getStore();
  if (!store || typeof store.getState !== "function") return true;
  const state = store.getState();
  const conversationPermissions = state?.userDetails?.userPermissions?.permissions?.Conversation || {};
  if (!conversationPermissions || Object.keys(conversationPermissions).length === 0) return true;
  if (conversationPermissions.ViewAll === true) return true;

  const contactId = normalizeId(
    messageCardData?.contactId ?? messageCardData?.contactID ??
    messageCardData?.chat?.contactId ?? messageCardData?.chat?.contactID
  );
  const inboxData = state?.inboxRecent?.data;
  // A card already in the list stays live for ordinary updates, but an assignment event must
  // be re-checked: the card is always in the list by definition, so the shortcut would let a
  // reassignment away from this user keep (and re-assign) a conversation they can't view.
  const isAssignmentEvent = messageCardData?.lastMessage?.type === "assignment";
  if (!isAssignmentEvent && contactId && inboxData && checkContactExistsInInboxData(inboxData, contactId)) return true;

  const currentUserId = normalizeId(
    state?.auth?.user?._id ?? state?.auth?.user?.id ?? state?.userDetails?.userDetails?._id
  );
  const { assignedToId, assignedToType, isUnassigned } = getAssignedTargetFromPayload(messageCardData);
  if (isUnassigned) return true;
  // ViewAssignedOnly is additive to the team scopes below: a role holding both must not be
  // cut off from team-assigned conversations, so only a match short-circuits here.
  const isAssignedToCurrentUser = Boolean(currentUserId && assignedToId && currentUserId === assignedToId);
  if (conversationPermissions.ViewAssignedOnly === true) {
    if (isAssignedToCurrentUser) return true;
    if (
      conversationPermissions.ViewAssignedToTeamMembers !== true &&
      conversationPermissions.ViewAssignedToTeam !== true
    ) {
      return false;
    }
  }
  if (conversationPermissions.ViewAssignedToTeamMembers === true) {
    if (currentUserId && assignedToId === currentUserId) return true;
    if (assignedToType === "team") return false;
    // Only conversations assigned to a teammate (see teammateIds in getUserDetails.service.js).
    // Falls back to allowing when the list hasn't loaded, so nothing is hidden by mistake.
    const teammateIds = state?.userDetails?.userDetails?.teammateIds;
    return !Array.isArray(teammateIds) || teammateIds.includes(assignedToId);
  }
  if (conversationPermissions.ViewAssignedToTeam === true) {
    // Only conversations assigned to a team this user belongs to (see teamIds in
    // getUserDetails.service.js). Falls back to allowing when the list hasn't loaded.
    const teamIds = state?.userDetails?.userDetails?.teamIds;
    return !Array.isArray(teamIds) || teamIds.includes(assignedToId);
  }
  return false;
};

// ─── misc helpers ─────────────────────────────────────────────────────────────
export const shouldCountAsUnread = (msg) => {
  const isReceived = (msg.clientname || msg.clientid) && !(msg.sendername || msg.senderid);
  const isUnread = msg.status !== "read" && msg.status !== "seen";
  const isNormalMessage = msg.type === "chat" || msg.type === "list" || !msg.type;
  return isReceived && isUnread && isNormalMessage;
};

export const getTimestampISO = (ts) => {
  if (!ts) return new Date().toISOString();
  const date = ts instanceof Date ? ts : new Date(ts);
  return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
};

// ─── active chat management ───────────────────────────────────────────────────
export const setActiveChat = (contactId) => {
  try {
    if (!socketState.socket || !contactId) return;
    socketState.activeChat = contactId;
    const parsedUser = getCachedUserData();
    const accountId = parsedUser?.accountId;
    if (!accountId) return;
    socketState.socket.emit("chat_opened", { contactId, accountId });
  } catch (_) {}
};

export const clearActiveChat = () => {
  socketState.activeChat = null;
  if (socketState.socket) socketState.socket.emit("chat_closed");
};

export const getActiveChat = () => socketState.activeChat;

// ─── message transformation ───────────────────────────────────────────────────
export const transformSocketMessage = (message) => {
  const messageWithLocalTime = convertMessageTimestamps(message);

  const isInternalMention = messageWithLocalTime.chat?.isInternalMention ||
    messageWithLocalTime.chat?.status === "internal" ||
    messageWithLocalTime.isInternalMention ||
    messageWithLocalTime.status === "internal";

  const fieldsToExcludeForInternal = isInternalMention
    ? ['status', 'assignedTo', 'assignedToName', 'assignedToId', 'assignedToType']
    : [];
  const messageWithoutInternalFields = fieldsToExcludeForInternal.length > 0
    ? Object.fromEntries(Object.entries(messageWithLocalTime).filter(([key]) => !fieldsToExcludeForInternal.includes(key)))
    : messageWithLocalTime;

  const transformed = {
    ...messageWithoutInternalFields,
    ...(!isInternalMention && messageWithLocalTime.assignedTo !== undefined && { assignedTo: messageWithLocalTime.assignedTo }),
    ...(!isInternalMention && messageWithLocalTime.assignedToName !== undefined && { assignedToName: messageWithLocalTime.assignedToName }),
    ...(!isInternalMention && messageWithLocalTime.assignedToId !== undefined && { assignedToId: messageWithLocalTime.assignedToId }),
    ...(!isInternalMention && messageWithLocalTime.assignedToType !== undefined && { assignedToType: messageWithLocalTime.assignedToType }),
    ...(!isInternalMention && messageWithLocalTime.status !== undefined && { status: messageWithLocalTime.status }),
    ...(messageWithLocalTime.closeStatus !== undefined && { closeStatus: messageWithLocalTime.closeStatus }),
    ...(messageWithLocalTime.previousAssignedToId !== undefined && { previousAssignedToId: messageWithLocalTime.previousAssignedToId }),
    ...(messageWithLocalTime.previousAssignedToType !== undefined && { previousAssignedToType: messageWithLocalTime.previousAssignedToType }),
    ...(messageWithLocalTime.contactName && { contactName: messageWithLocalTime.contactName }),
    ...(messageWithLocalTime.contactNumber && { contactNumber: messageWithLocalTime.contactNumber }),
    ...(messageWithLocalTime.phoneNumber && { phoneNumber: messageWithLocalTime.phoneNumber }),
    ...(messageWithLocalTime.phoneNumberID && { phoneNumberID: messageWithLocalTime.phoneNumberID }),
    ...(messageWithLocalTime.workspaceId !== undefined && { workspaceId: messageWithLocalTime.workspaceId }),
  };

  if (!transformed.chat && messageWithLocalTime.message) {
    transformed.chat = { ...messageWithLocalTime.message };
    if (messageWithLocalTime.message.type === "status_update" && !transformed.type) transformed.type = messageWithLocalTime.message.type;
    if (messageWithLocalTime.message.sendername && !transformed.chat.sendername) transformed.chat.sendername = messageWithLocalTime.message.sendername;
    const srcMsgSid = messageWithLocalTime.message?.senderid || messageWithLocalTime.message?.senderId;
    if (srcMsgSid && !transformed.chat.senderid) transformed.chat.senderid = srcMsgSid;
    if (messageWithLocalTime.message.senderAvatarColor && !transformed.chat.senderAvatarColor) transformed.chat.senderAvatarColor = messageWithLocalTime.message.senderAvatarColor;
    if (messageWithLocalTime.message.isInternalMention !== undefined) transformed.chat.isInternalMention = messageWithLocalTime.message.isInternalMention;
    if (messageWithLocalTime.message.status === "internal") { transformed.chat.status = "internal"; transformed.chat.isInternalMention = true; }
  }

  if (!transformed.chat && messageWithLocalTime.type === "status_update") {
    transformed.chat = {
      type: messageWithLocalTime.type,
      id: messageWithLocalTime.id,
      tempId: messageWithLocalTime.tempId,
      status: messageWithLocalTime.status,
      oldStatus: messageWithLocalTime.oldStatus,
      timestamp: messageWithLocalTime.timestamp
    };
    if (!transformed.type) transformed.type = messageWithLocalTime.type;
  }

  if (transformed.chat) {
    transformed.chat = { ...transformed.chat };

    if (messageWithLocalTime.chat?.sendername && !transformed.chat.sendername) transformed.chat.sendername = messageWithLocalTime.chat.sendername;
    const srcChatSid = messageWithLocalTime.chat?.senderid || messageWithLocalTime.chat?.senderId;
    if (srcChatSid && !transformed.chat.senderid) transformed.chat.senderid = srcChatSid;
    if (messageWithLocalTime.chat?.senderAvatarColor && !transformed.chat.senderAvatarColor) transformed.chat.senderAvatarColor = messageWithLocalTime.chat.senderAvatarColor;
    if (messageWithLocalTime.chat?.isInternalMention !== undefined) transformed.chat.isInternalMention = messageWithLocalTime.chat.isInternalMention;
    if (messageWithLocalTime.chat?.status === "internal" || transformed.chat.status === "internal") {
      transformed.chat.status = "internal"; transformed.chat.isInternalMention = true;
    }

    if (!transformed.chat.message && transformed.chat.body) transformed.chat.message = transformed.chat.body;
    if (!transformed.chat.body && transformed.chat.message) transformed.chat.body = transformed.chat.message;
    if (transformed.chat.type === "list" && transformed.chat.body && !transformed.chat.message) transformed.chat.message = transformed.chat.body;

    const isTemplateMessage = transformed.chat.type === "template" || transformed.chat.messageType === "template";
    const isListMessage = transformed.chat.type === "list" || transformed.chat.messageType === "list";
    const isCatalogMessage = transformed.chat.type === "catalog" || transformed.chat.messageType === "catalog";

    let detectedMessageType = null;
    const messageContent = transformed.chat.message || transformed.chat.body || "";
    if (!isTemplateMessage && !isListMessage && !isCatalogMessage && messageContent && typeof messageContent === 'string' && messageContent.trim()) {
      const isCloudinaryUrl = messageContent.match(/cloudinary\.com/i);
      if (isCloudinaryUrl) {
        if (messageContent.match(/\.(mp3|wav|ogg|m4a|aac|flac|oga|opus|amr|3gp)(\?|$)/i)) detectedMessageType = "audio";
        else if (messageContent.match(/\.(mp4|webm|mov|avi|wmv|flv|mkv)(\?|$)/i)) detectedMessageType = "video";
        else if (messageContent.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i)) detectedMessageType = "image";
        else if (messageContent.match(/\/audio\//i)) detectedMessageType = "audio";
        else if (messageContent.match(/\/video\//i)) detectedMessageType = "video";
        else if (messageContent.match(/\/image\//i)) detectedMessageType = "image";
      } else {
        if (messageContent.match(/\/audio\//i)) detectedMessageType = "audio";
        else if (messageContent.match(/\/video\//i)) detectedMessageType = "video";
        else if (messageContent.match(/\/image\//i)) detectedMessageType = "image";
        else if (messageContent.match(/\.(mp3|wav|ogg|m4a|aac|flac|oga|opus|amr|3gp)(\?|$)/i)) detectedMessageType = "audio";
        else if (messageContent.match(/\.(mp4|webm|mov|avi|wmv|flv|mkv)(\?|$)/i)) detectedMessageType = "video";
        else if (messageContent.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i)) detectedMessageType = "image";
      }
      if (!detectedMessageType && messageContent.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf)(\?|$)/i)) detectedMessageType = "document";
      if (!detectedMessageType && messageContent.trim().startsWith("{")) {
        try {
          const p = JSON.parse(messageContent);
          const lat = Number(p?.latitude); const lng = Number(p?.longitude);
          if (p && typeof p === "object" && !Number.isNaN(lat) && !Number.isNaN(lng) && !p.productId && !p.catalogId && !p.items && !p.allProducts && !p.components && !p.catalogType && !p.productData && !Array.isArray(p.rows)) {
            detectedMessageType = "location";
          } else if (Array.isArray(p.contacts) && p.contacts.length > 0 && p.contacts[0] && typeof p.contacts[0] === "object" && (p.contacts[0].name || p.contacts[0].phones)) {
            detectedMessageType = "contacts";
          }
        } catch (_) {}
      }
    }

    if (isListMessage) {
      transformed.chat.messageType = "list"; transformed.chat.type = "chat";
    } else if (isTemplateMessage) {
      transformed.chat.messageType = "template";
      if (transformed.chat.type !== "template") transformed.chat.type = "template";
    } else if (isCatalogMessage) {
      transformed.chat.messageType = "catalog"; transformed.chat.type = "chat";
    } else if (detectedMessageType) {
      transformed.chat.messageType = detectedMessageType;
    } else if (!transformed.chat.messageType || transformed.chat.messageType === "chat") {
      if (transformed.chat.type && transformed.chat.type !== "chat") {
        transformed.chat.messageType = transformed.chat.type;
      } else {
        transformed.chat.messageType = "text";
      }
    } else {
      const mc = transformed.chat.body || transformed.chat.message || "";
      let isCatalogPurchase = transformed.chat.type === "catalog_purchase" || transformed.chat.messageType === "catalog_purchase";
      if (!isCatalogPurchase && mc && typeof mc === 'string' && mc.trim()) {
        try {
          const parsedBody = JSON.parse(mc);
          if (parsedBody && typeof parsedBody === 'object' && (parsedBody.productId || parsedBody.catalogId || parsedBody.allProducts || parsedBody.items)) isCatalogPurchase = true;
        } catch (_) {}
      }
      if (isCatalogPurchase) { transformed.chat.messageType = "catalog_purchase"; transformed.chat.type = "chat"; }
    }

    if (!transformed.chat.message || !transformed.chat.message.trim()) {
      if (transformed.chat.body && transformed.chat.body.trim()) transformed.chat.message = transformed.chat.body;
    }
    if (!transformed.chat.body || !transformed.chat.body.trim()) {
      if (transformed.chat.message && transformed.chat.message.trim()) transformed.chat.body = transformed.chat.message;
    }
    if (transformed.chat.messageType && ["image", "video", "audio", "document", "location", "contacts", "text"].includes(transformed.chat.messageType)) {
      transformed.chat.type = "chat";
    }

    if (!transformed.chat.time) transformed.chat.time = transformed.chat.timestamp;
    if (transformed.chat.timestamp && transformed.chat.timestamp instanceof Date) transformed.chat.timestamp = transformed.chat.timestamp.toISOString();
    if (transformed.chat.time && transformed.chat.time instanceof Date) transformed.chat.time = transformed.chat.time.toISOString();

    if (messageWithLocalTime.chat?.createdAt) {
      transformed.chat.createdAt = messageWithLocalTime.chat.createdAt;
    } else if (!transformed.chat.createdAt && transformed.chat.timestamp) {
      transformed.chat.createdAt = transformed.chat.timestamp;
    }

    if (!transformed.chat.replyId && transformed.chat.replyTo) transformed.chat.replyId = transformed.chat.replyTo;
    if (!transformed.chat.replyTo && transformed.chat.replyId) transformed.chat.replyTo = transformed.chat.replyId;
    if (messageWithLocalTime.chat?.replySenderName !== undefined) transformed.chat.replySenderName = messageWithLocalTime.chat.replySenderName;
    if (messageWithLocalTime.chat?.replyBody !== undefined) transformed.chat.replyBody = messageWithLocalTime.chat.replyBody;
    if (messageWithLocalTime.chat?.replyType !== undefined) transformed.chat.replyType = messageWithLocalTime.chat.replyType;
    if (!transformed.chat._id) transformed.chat._id = transformed.chat.id || Date.now().toString();

    if (messageWithLocalTime.chat) {
      if (messageWithLocalTime.chat.toName !== undefined) transformed.chat.toName = messageWithLocalTime.chat.toName;
      if (messageWithLocalTime.chat.to !== undefined) transformed.chat.to = messageWithLocalTime.chat.to;
      // Normalized for every type, not just "assignment" — the calling feature's
      // ChatBody renderer (type "call") reads chat.date too, and relying on each
      // new type to remember to set it server-side is exactly how that one shipped
      // without it. timestamp/time are guaranteed present (set unconditionally by
      // transformMessageForSocket), so this is a safe universal fallback.
      let dateValue;
      if (messageWithLocalTime.chat.date) {
        dateValue = messageWithLocalTime.chat.date instanceof Date ? messageWithLocalTime.chat.date : new Date(messageWithLocalTime.chat.date);
      } else if (transformed.chat.timestamp) {
        dateValue = transformed.chat.timestamp instanceof Date ? transformed.chat.timestamp : new Date(transformed.chat.timestamp);
      } else if (transformed.chat.time) {
        dateValue = transformed.chat.time instanceof Date ? transformed.chat.time : new Date(transformed.chat.time);
      } else {
        dateValue = new Date();
      }
      transformed.chat.date = dateValue instanceof Date && !isNaN(dateValue.getTime())
        ? dateValue.toISOString()
        : (typeof dateValue === 'string' ? dateValue : new Date().toISOString());
    }
  }

  if (transformed.chat && transformed.chat.type === "assignment") {
    const dateValue = transformed.chat.date || transformed.chat.timestamp || transformed.chat.time;
    if (dateValue) {
      transformed.date = dateValue instanceof Date ? dateValue.toISOString() : (typeof dateValue === 'string' ? dateValue : new Date(dateValue).toISOString());
    } else {
      transformed.date = new Date().toISOString();
    }
  }

  return transformed;
};
